import React, { useEffect, useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { Box, Button, DialogActions, DialogContent, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { PathFundSelection, replacePathsInProduct } from "../../../hooks/feeLab/useNewProductHandlers";
import CurrencyTextField from "../../../shared/UIParts/InputFields/CurrencyTextField";
import { formatCurrency } from "../../../utils/namesFormater";
import PolicyPickList from "./PolicyPickList";
import NewFundForm, { NewFundContactOption } from "./NewFundForm";
import PathChooser from "./PathChooser";
import TargetFeesFields, { hasDepositFee, useProposedSavingFee } from "./TargetFeesFields";
import {
  OneTimeDepositAction,
  RecurringDepositAction,
  WithdrawalAction,
  isBasketNewFund,
  newActionId,
  policyKey,
  policyLabel,
  productLabel,
  sumFunds,
} from "./quickActions";

export type AmountActionKind = "one_time_deposit" | "recurring_deposit" | "withdrawal";

/** pick - which fund, new_fund - opening one, amount - how much, setup - a new fund's fees and paths */
type Step = "pick" | "new_fund" | "amount" | "setup";

/** the days of the month a standing order can be charged on (the producers' collection days) */
const RECURRENT_DEPOSIT_DAYS = [1, 10, 15];

const AMOUNT_LABEL: Record<AmountActionKind, string> = {
  one_time_deposit: "סכום ההפקדה",
  recurring_deposit: "הוראת קבע חדשה",
  withdrawal: "סכום המשיכה",
};

interface Props {
  kind: AmountActionKind;
  policies: MimshakData[];
  onComplete: (action: OneTimeDepositAction | RecurringDepositAction | WithdrawalAction) => void;
  onCancel: () => void;
  /** under the policy list (e.g. "הצג הכל" when the policies are filtered) */
  policiesFooter?: React.ReactNode;
  /** a deposit / standing order can go into a new fund - whose it can be */
  newFundContacts?: NewFundContactOption[];
}

/**
 * הפקדה חד פעמית / הוראת קבע / משיכה: לאיזו קופה (קיימת, או בהפקדה ובהוראת קבע – קופה חדשה) → סכום →
 * בקופה חדשה: דמי ניהול (מוצעים לפי הסכם העמלות, לפי הסכום) ומסלול. רק בונה את הפעולה ומחזיר אותה ב-onComplete.
 */
const AmountActionForm: React.FC<Props> = ({ kind, policies, onComplete, onCancel, policiesFooter, newFundContacts = [] }) => {
  const [step, setStep] = useState<Step>("pick");
  const [policy, setPolicy] = useState<MimshakData | null>(null);
  // the fund marked in the list - "לקופה שנבחרה" continues with it
  const [markedPolicy, setMarkedPolicy] = useState<MimshakData | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  // a new standing order: the day of the month it is charged (filled into the producer's form)
  const [dayOfMonth, setDayOfMonth] = useState<number | null>(null);
  // a new fund's setup
  const [savingFee, setSavingFee] = useState<number | null>(null);
  const [depositFee, setDepositFee] = useState<number | null>(null);
  const [feesTouched, setFeesTouched] = useState(false);
  const [selections, setSelections] = useState<PathFundSelection[]>([]);
  // the fund was opened here - it needs its fees and paths (a new fund opened earlier in the basket already has them)
  const [isNewFund, setIsNewFund] = useState(false);

  const canOpenNewFund = kind !== "withdrawal" && newFundContacts.length > 0;
  const currentRecurrent = policy?.recurrent_deposit ?? null;
  const isNewStandingOrder = kind === "recurring_deposit" && !currentRecurrent;
  const balance = policy?.funds_sum || 0;
  const productType = policy ? policy.adjusted_product_type ?? policy.product_type : null;

  // the new fund with the money it gets - a deposit is its balance (a standing order - none yet)
  const fundWithAmount = useMemo(
    () => (policy && isNewFund ? { ...policy, funds_sum: kind === "one_time_deposit" ? amount ?? 0 : 0 } : null),
    [policy, isNewFund, kind, amount]
  );

  // the proposed fee - for the client's total at the handler with this money
  const alreadyAtHandler = useMemo(
    () => (policy ? sumFunds(policies.filter((p) => p.handler_code === policy.handler_code && policyKey(p) !== policyKey(policy))) : 0),
    [policies, policy]
  );
  const proposedSavingFee = useProposedSavingFee(isNewFund ? policy!.handler_code : "", alreadyAtHandler + (fundWithAmount?.funds_sum ?? 0));

  // entering a new fund's setup - the proposed fee, until the agent changes it
  useEffect(() => {
    if (step === "setup" && !feesTouched) setSavingFee(proposedSavingFee);
  }, [step, proposedSavingFee, feesTouched]);

  const error =
    amount == null
      ? null
      : amount <= 0
      ? "יש להזין סכום גדול מאפס"
      : kind === "recurring_deposit" && amount === currentRecurrent
      ? "הסכום זהה להוראת הקבע הנוכחית"
      : kind === "withdrawal" && amount > balance
      ? `הסכום גדול מהיתרה (${formatCurrency(balance)})`
      : null;
  // a new standing order also needs its charge day (filled into the producer's form)
  const amountValid = !!policy && amount != null && !error && (!isNewStandingOrder || dayOfMonth != null);

  const pickPolicy = (p: MimshakData, opened = false) => {
    setPolicy(p);
    setIsNewFund(opened);
    setSelections([]);
    setFeesTouched(false);
    setStep("amount");
  };

  /** a new fund - its fees and paths on it */
  const finalizeNewFund = (fund: MimshakData): MimshakData => {
    const withFees = { ...fund, saving_fee: savingFee, deposit_fee: hasDepositFee(productType) ? depositFee ?? 0 : 0 };
    if (selections.length === 0) return withFees;
    const withPaths = cloneDeep(withFees);
    replacePathsInProduct(withPaths, fund.policy_number, selections);
    return { ...withPaths, fund_ids: selections.map((s) => s.fund.fund_id) };
  };

  const handleComplete = () => {
    if (!policy || amount == null || !amountValid) return;
    const target = fundWithAmount ? finalizeNewFund(fundWithAmount) : policy;
    const id = newActionId();
    onComplete(
      kind === "recurring_deposit"
        ? { kind, id, policy: target, newAmount: amount, dayOfMonth: isNewStandingOrder ? dayOfMonth : null }
        : kind === "withdrawal"
        ? { kind, id, policy: target, amount }
        : { kind, id, policy: target, amount }
    );
  };

  const backToPick = () => {
    setPolicy(null);
    // back to the list with the fund still marked - unless it was opened here (it is not in the list)
    setMarkedPolicy(isNewFund ? null : policy);
    setStep("pick");
  };

  if (step === "new_fund") {
    return (
      <NewFundForm
        contacts={newFundContacts}
        reportedPeriod={policies[0]?.reported_period ?? new Date().toISOString().split("T")[0]}
        onCreate={(fund) => pickPolicy(fund, true)}
        onCancel={() => setStep("pick")}
      />
    );
  }

  if (step === "pick" || !policy) {
    return (
      <>
        <DialogContent sx={{ p: 0 }}>
          <Typography variant="subtitle2" sx={{ px: 3, pt: 1 }}>
            {kind === "withdrawal" ? "מאיזו קופה?" : "לאיזו קופה?"}
          </Typography>
          <PolicyPickList policies={policies} selected={markedPolicy} onPick={setMarkedPolicy} footer={policiesFooter} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={onCancel} variant="outlined" size="large">
            חזור
          </Button>
          {canOpenNewFund && (
            <Button onClick={() => setStep("new_fund")} variant="outlined" size="large" startIcon={<AddIcon sx={{ ml: 1 }} />}>
              לקופה חדשה
            </Button>
          )}
          <Button
            onClick={() => markedPolicy && pickPolicy(markedPolicy)}
            disabled={!markedPolicy}
            variant="contained"
            size="large"
          >
            {kind === "withdrawal" ? "מהקופה שנבחרה" : "לקופה שנבחרה"}
          </Button>
        </DialogActions>
      </>
    );
  }

  const fundHeader = (
    <Box>
      <Typography variant="body2" color="text.secondary">
        קופה
      </Typography>
      <Typography variant="body1" fontWeight={600}>
        {policyLabel(policy)}
        {isBasketNewFund(policy) ? " · קופה חדשה" : ""}
      </Typography>
      {!isNewFund && (
        <Typography variant="body2" color="text.secondary">
          {kind === "recurring_deposit"
            ? `הוראת קבע נוכחית: ${currentRecurrent != null ? formatCurrency(currentRecurrent) : "אין"}`
            : `יתרה: ${formatCurrency(balance)}`}
        </Typography>
      )}
      {amount != null && step === "setup" && (
        <Typography variant="body2" color="text.secondary">
          {AMOUNT_LABEL[kind]}: {formatCurrency(amount)}
        </Typography>
      )}
      <Button size="small" onClick={backToPick} sx={{ px: 0 }}>
        החלף קופה
      </Button>
    </Box>
  );

  if (step === "setup" && fundWithAmount) {
    return (
      <>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {fundHeader}
            <TargetFeesFields
              productType={productType}
              proposedSavingFee={proposedSavingFee}
              savingFee={savingFee}
              depositFee={depositFee}
              onSavingFeeChange={(fee) => {
                setFeesTouched(true);
                setSavingFee(fee);
              }}
              onDepositFeeChange={setDepositFee}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                מסלול – {productLabel(fundWithAmount)}
              </Typography>
              <PathChooser policy={fundWithAmount} selections={selections} onSelectionsChange={setSelections} showCurrentPaths={false} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setStep("amount")} variant="outlined" size="large">
            חזור
          </Button>
          <Button onClick={handleComplete} disabled={!amountValid} variant="contained" size="large">
            הוסף
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {fundHeader}
          <CurrencyTextField
            label={AMOUNT_LABEL[kind]}
            value={amount}
            onChange={setAmount}
            size="medium"
            fullWidth
            error={!!error}
            helperText={error ?? undefined}
          />
          {isNewStandingOrder && (
            <Box>
              <FormLabel>יום החיוב בחודש</FormLabel>
              <RadioGroup row value={dayOfMonth ?? ""} onChange={(e) => setDayOfMonth(Number(e.target.value))}>
                {RECURRENT_DEPOSIT_DAYS.map((d) => (
                  <FormControlLabel key={d} value={d} control={<Radio />} label={`${d} לחודש`} />
                ))}
              </RadioGroup>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={backToPick} variant="outlined" size="large">
          חזור
        </Button>
        {isNewFund ? (
          // a new fund - its fees and path, now that the amount is known
          <Button onClick={() => setStep("setup")} disabled={!amountValid} variant="contained" size="large">
            המשך
          </Button>
        ) : (
          <Button onClick={handleComplete} disabled={!amountValid} variant="contained" size="large">
            הוסף
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default AmountActionForm;
